import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import { findRelatedBlogs, formatDate } from '../utils/findRelatedBlogs';

/**
 * RelatedBlogs Component
 * Displays blogs related to a service page with preview and read more functionality
 * 
 * @param {Object} props
 * @param {string} props.servicePath - The path of the service (e.g., '/services/web-development')
 * @param {Array} props.serviceTags - Array of tags related to the service
 * @param {string} props.serviceName - Name of the service (optional)
 * @param {number} props.limit - Maximum number of blogs to display (default: 6)
 * @param {string} props.title - Section title (default: 'Related Blogs')
 */
const RelatedBlogs = ({ 
  servicePath, 
  serviceTags = [], 
  serviceName = '', 
  limit = 6,
  title = 'Related Blogs'
}) => {
  const navigate = useNavigate();
  const [allBlogs, setAllBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [previewBlog, setPreviewBlog] = useState(null);

  // Load all blogs
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setIsLoading(true);
        setAllBlogs([]);
        setLoadedCount(0);
        
        // Use Vite's import.meta.glob to dynamically import all blog files
        const blogModules = import.meta.glob('../blogs/*.jsx');
        const blogPaths = Object.keys(blogModules);
        setTotalBlogs(blogPaths.length);
        
        if (blogPaths.length === 0) {
          setIsLoading(false);
          return;
        }
        
        const loadedBlogIds = new Set();
        let completedCount = 0;
        
        const processBlog = (blogData) => {
          if (blogData && blogData.slug && !loadedBlogIds.has(blogData.slug)) {
            loadedBlogIds.add(blogData.slug);
            completedCount++;
            setLoadedCount(completedCount);
            
            setAllBlogs(prevBlogs => {
              const updated = [...prevBlogs, blogData];
              updated.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
              return updated;
            });
          } else {
            completedCount++;
            setLoadedCount(completedCount);
          }
        };
        
        const blogPromises = blogPaths.map(async (path) => {
          try {
            const module = await blogModules[path]();
            const blogData = Object.values(module)[0];
            processBlog(blogData);
          } catch (error) {
            console.error(`Error loading blog from ${path}:`, error);
            completedCount++;
            setLoadedCount(completedCount);
          }
        });
        
        await Promise.all(blogPromises);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading blogs:', error);
        setIsLoading(false);
      }
    };

    loadBlogs();
  }, []);

  // Find related blogs
  const relatedBlogs = useMemo(() => {
    if (isLoading || allBlogs.length === 0) {
      return [];
    }

    return findRelatedBlogs(
      allBlogs,
      {
        servicePath,
        serviceTags,
        serviceName
      },
      limit
    );
  }, [allBlogs, servicePath, serviceTags, serviceName, limit, isLoading]);

  // Don't render if no related blogs found
  if (!isLoading && relatedBlogs.length === 0) {
    return null;
  }

  return (
    <section id="related-blogs" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 md:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
            {title}
          </h2>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Explore our latest articles and insights related to this service
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-flex flex-col items-center gap-4">
              <div className="relative">
                <div 
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: '4px solid #f1f5f9', 
                    borderTop: '4px solid #00d690', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite',
                  }}
                ></div>
              </div>
              <p className="text-sm sm:text-base text-gray-500">
                Loading blogs... ({loadedCount}/{totalBlogs})
              </p>
            </div>
          </div>
        ) : relatedBlogs.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {relatedBlogs.map((blog) => (
              <div
                key={blog.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                }}
                onClick={() => setPreviewBlog(blog)}
              >
                {/* Blog Header */}
                <div className="p-4 sm:p-5 md:p-6">
                  <div className="flex gap-2 sm:gap-3 mb-3 sm:mb-4 flex-wrap">
                    {blog.category && (
                      <span className="bg-[#00d690] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        {blog.category}
                      </span>
                    )}
                    {blog.readTime && (
                      <span className="bg-gray-100 text-gray-500 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                        {blog.readTime}
                      </span>
                    )}
                  </div>

                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 leading-snug line-clamp-2">
                    {blog.title}
                  </h3>

                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed line-clamp-3 mb-3 sm:mb-4">
                    {blog.shortDescription}
                  </p>
                </div>

                {/* Blog Footer */}
                <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 mt-auto border-t border-gray-100">
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap mb-2 sm:mb-3">
                    {blog.publishDate && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Icon name="Calendar" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm text-gray-500">
                          {formatDate(blog.publishDate)}
                        </span>
                      </div>
                    )}
                    {blog.readTime && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Icon name="Clock" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm text-gray-500">
                          {blog.readTime}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Tags */}
                  {blog.tags && blog.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                      {blog.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                      {blog.tags.length > 3 && (
                        <span className="bg-gray-50 text-gray-600 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium">
                          +{blog.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Preview and Read More Buttons */}
                  <div className="flex gap-2 sm:gap-3 justify-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewBlog(blog);
                      }}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs sm:text-sm py-2 px-3 sm:px-4 rounded-lg transition-colors"
                    >
                      Preview
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/blog/${blog.slug}`);
                      }}
                      className="flex-1 bg-[#00d690] hover:bg-[#00b876] text-white font-semibold text-xs sm:text-sm py-2 px-3 sm:px-4 rounded-lg transition-colors inline-flex items-center justify-center gap-1 sm:gap-2"
                    >
                      Read More
                      <Icon name="ArrowRight" size={14} className="sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Preview Modal */}
        {previewBlog && (
          <div
            className="fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4 sm:p-5 md:p-6"
            style={{ animation: 'fadeIn 0.3s ease' }}
            onClick={() => setPreviewBlog(null)}
          >
            <div
              className="bg-white rounded-xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              style={{ animation: 'slideUp 0.3s ease' }}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-start justify-between z-10">
                <div className="flex-1">
                  <div className="flex gap-2 sm:gap-3 mb-2 sm:mb-3 flex-wrap">
                    {previewBlog.category && (
                      <span className="bg-[#00d690] text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                        {previewBlog.category}
                      </span>
                    )}
                    {previewBlog.readTime && (
                      <span className="bg-gray-100 text-gray-500 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                        {previewBlog.readTime}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3">
                    {previewBlog.title}
                  </h2>
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-xs sm:text-sm text-gray-500">
                    {previewBlog.publishDate && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Icon name="Calendar" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                        <span>{formatDate(previewBlog.publishDate)}</span>
                      </div>
                    )}
                    {previewBlog.readTime && (
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <Icon name="Clock" size={14} className="text-gray-500 sm:w-4 sm:h-4" />
                        <span>{previewBlog.readTime}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setPreviewBlog(null)}
                  className="ml-4 p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  aria-label="Close preview"
                >
                  <Icon name="X" size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6 md:p-8">
                {/* Article Description */}
                <div className="mb-6">
                  <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed font-medium">
                    {previewBlog.shortDescription}
                  </p>
                </div>

                {/* Blog Content Preview */}
                {previewBlog.content && (
                  <div className="mb-6">
                    <div className="prose prose-sm sm:prose-base max-w-none text-gray-700 leading-relaxed">
                      <div className="blog-content-preview" style={{ maxHeight: '450px', overflow: 'hidden', position: 'relative' }}>
                        {(() => {
                          // Helper function to extract text from React element
                          const extractText = (element, maxLength = 800) => {
                            if (!element) return '';
                            if (typeof element === 'string') return element;
                            if (React.isValidElement(element)) {
                              const children = React.Children.toArray(element.props.children || []);
                              return children.map(child => extractText(child, maxLength)).join(' ').substring(0, maxLength);
                            }
                            return '';
                          };

                          // Render first portion of content
                          if (React.isValidElement(previewBlog.content)) {
                            const contentChildren = React.Children.toArray(previewBlog.content.props.children);
                            let renderedCount = 0;
                            const maxToRender = 4; // Show first 4 meaningful elements
                            
                            return (
                              <div className="space-y-4">
                                {contentChildren.map((child, index) => {
                                  if (renderedCount >= maxToRender) return null;
                                  
                                  if (React.isValidElement(child)) {
                                    const childType = typeof child.type === 'string' ? child.type : 'component';
                                    
                                    // Skip style and script tags
                                    if (['style', 'script'].includes(childType)) {
                                      return null;
                                    }
                                    
                                    // Render paragraphs, headings, and divs with content
                                    if (['p', 'h1', 'h2', 'h3', 'h4', 'div'].includes(childType)) {
                                      const hasText = extractText(child, 50).trim().length > 0;
                                      if (hasText) {
                                        renderedCount++;
                                        // Clone element but limit its depth for preview
                                        return React.cloneElement(child, { 
                                          key: index,
                                          className: `${child.props.className || ''} preview-content`
                                        });
                                      }
                                    }
                                    
                                    // For other elements, try to render if they have content
                                    const text = extractText(child, 50);
                                    if (text.trim().length > 20) {
                                      renderedCount++;
                                      return <div key={index} className="preview-content">{child}</div>;
                                    }
                                  }
                                  
                                  return null;
                                })}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {/* Fade effect at bottom */}
                        <div 
                          className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none"
                        ></div>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-sm text-gray-500 italic">
                          ... continue reading to explore detailed insights, examples, and practical implementations
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Call to Action */}
                <div className="pt-6 border-t border-gray-200">
                  <button
                    onClick={() => navigate(`/blog/${previewBlog.slug}`)}
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white font-semibold text-base sm:text-lg py-4 px-8 rounded-lg transition-all duration-200 inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Read Full Article
                    <Icon name="ArrowRight" size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default RelatedBlogs;

